import { Button, Tooltip, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { refreshSessionAsync } from '@store/slices/authSlice';
import { useSessionCountdown } from '@hooks/useSessionCountdown';
import { useTheme } from '@hooks/useTheme';
import { useState } from 'react';

const { Text } = Typography;

function formatRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Header session indicator: stacked user name + colored countdown bar
 * with an inline refresh icon. Hidden when ``expiresAt`` is unavailable
 * (e.g. cookie-bootstrap edge case where ``auth.tokens`` is null).
 */
export function SessionTimeoutBar() {
  const user = useAppSelector((state) => state.auth.user);
  const dispatch = useAppDispatch();
  const { isDark } = useTheme();
  const { isActive, remainingMs, percentage, color } = useSessionCountdown();
  const [refreshing, setRefreshing] = useState(false);

  if (!user || !isActive) {
    return null;
  }

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await dispatch(refreshSessionAsync()).unwrap();
    } catch {
      /* surface via existing 401 / session-expired flow if it ever does */
    } finally {
      setRefreshing(false);
    }
  };

  const trackBg = isDark ? '#334155' : '#e2e8f0';
  const widthPct = Math.max(0, Math.min(100, percentage * 100));
  const remainingLabel = formatRemaining(remainingMs);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        // Tight vertical rhythm between the three rows (name → bar →
        // countdown). The previous ``gap: 4`` plus generous line
        // heights pushed the cluster taller than the rest of the
        // header icon row; ``gap: 1`` + trimmed line heights collapses
        // it to a more compact stack while still keeping the rows
        // visually distinct.
        gap: 1,
        width: 240,
        userSelect: 'none',
      }}
    >
      <Text
        strong
        ellipsis
        style={{ fontSize: 12, lineHeight: '14px', maxWidth: '100%' }}
      >
        {user.displayName}
      </Text>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Tooltip title={`${remainingLabel} remaining`} placement="bottom">
          <div
            role="progressbar"
            aria-label="Session time remaining"
            aria-valuenow={Math.round(widthPct)}
            aria-valuemin={0}
            aria-valuemax={100}
            style={{
              flex: 1,
              height: 6,
              borderRadius: 999,
              background: trackBg,
              overflow: 'hidden',
              cursor: 'help',
            }}
          >
            <div
              style={{
                width: `${widthPct}%`,
                height: '100%',
                background: color,
                transition: 'width 1s linear, background-color 1s linear',
              }}
            />
          </div>
        </Tooltip>

        <Tooltip title="Refresh session" placement="bottom">
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined spin={refreshing} />}
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label="Refresh session"
            style={{ width: 24, height: 24, padding: 0, minWidth: 0 }}
          />
        </Tooltip>
      </div>

      {/*
        Numeric countdown beneath the bar. The exact same MM:SS value
        as the tooltip so users get the time at a glance without
        having to hover. Sessions cap at SESSION_TOTAL_MS (60 min) so
        MM:SS comfortably fits the worst case (``60:00``).
      */}
      <Text
        type="secondary"
        style={{ fontSize: 11, lineHeight: '13px', fontVariantNumeric: 'tabular-nums' }}
        aria-live="off"
      >
        Session expires in {remainingLabel}
      </Text>
    </div>
  );
}
